import { useState } from 'react'
import { GitBranch, Copy, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

export interface SessionBranchInfoProps {
  /** Branch name to display */
  branch?: string
  /** Additional CSS classes */
  className?: string
}

/**
 * Displays session branch information with copy functionality.
 * Returns null if branch is not provided.
 *
 * @example
 * ```tsx
 * <SessionBranchInfo branch="agent/run_20251225_143052_abc123" />
 * ```
 */
export function SessionBranchInfo({ branch, className }: SessionBranchInfoProps) {
  const [copied, setCopied] = useState(false)

  if (!branch) {
    return null
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(branch)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy branch name:', err)
    }
  }

  return (
    <div className={cn('space-y-1', className)}>
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <GitBranch className="h-4 w-4" data-testid="branch-icon" />
        <span>Branch</span>
      </div>
      <div className="flex items-center gap-2">
        <span
          data-testid="branch-name"
          title={branch}
          className="truncate text-sm font-mono flex-1 min-w-0"
        >
          {branch}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0"
          onClick={handleCopy}
          aria-label="Copy branch name"
        >
          {copied ? (
            <Check className="h-3 w-3 text-green-500" data-testid="copy-success" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
        </Button>
      </div>
    </div>
  )
}
